import cn from 'classnames';
import React, { useState, useEffect } from 'react';
import { Formik, Field } from 'formik';
import { useParams, useHistory } from 'react-router-dom';

/* i18n */
import { useIntl } from 'react-intl';

/* API */
import { postProfile } from 'lib/API';

/* Styles */
import styles from 'styles/recommendations-list.module.scss';
import cardsStyles from 'styles/experience-cards.module.scss';
import formStyles from 'styles/experience-form.module.scss';
import layoutStyles from 'styles/layout.module.scss';
import stepperStyles from 'styles/stepper.module.scss';
import industryCardsStyles from 'styles/industry-cards.module.scss';

/* Analytics */
import { trackEvent } from 'lib/analytics';
import TimeMe from "timeme.js";

/* Material UI and other UI Dependencies */
import CheckIcon from '@material-ui/icons/Check';
import {
  Box,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Container,
  Typography,
  Modal,
  Backdrop,
  Fade
} from '@material-ui/core';

/* Redux */
import { useSelector, useDispatch } from 'react-redux';
import { thumbsUpClick, thumbsDownClick, revertThumbsClick } from 'app/slices/ratingsSlice';
import { setGoal, setCareers } from 'app/slices/careersSlice';
import { setJobs } from 'app/slices/jobsSlice';
import { setTraining } from 'app/slices/trainingSlice';
import { setExperienceForm, setExperienceSkills, setExperienceHasResume } from 'app/slices/experienceSlice';
import { setLoadingRecommendations } from 'app/slices/appSlice';

/* API */
import { rateItem, postUpload } from 'lib/API';

/* UI Components */
import ExperienceForm from 'components/forms/Experience';
import UploadResume from './forms/UploadResume';
import { useSnackbar } from 'notistack';
import GoalCard from 'components/GoalCard';
import SkillCard from 'components/SkillCard';

function StepIcon(props) {
  const { active, completed } = props;

  return (
    <div
      className={cn(stepperStyles.root, {
        [stepperStyles.active]: active,
        [stepperStyles.completed]: completed,
      })}
    >
      {active || completed ? (
        <CheckIcon />
      ) : null}
    </div>
  );
}

const Experience = (props) => {

  const intl = useIntl();
  const urlPrefix = intl.locale === 'en' ? '' : `/${intl.locale}`;
  const [uploading, setUploading] = useState(false);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { lang } = useParams();
  const history = useHistory();

  if (lang === 'onboarding') {
    history.push(`${urlPrefix}/careers`);
  }

  const skills = useSelector((state) => state.experience.skills);
  const goal = useSelector((state) => state.careers.goal);
  const ratingsLookup = useSelector((state) => state.ratings.results.lookup);
  const experienceForm = useSelector((state) => state.experience.form);
  const experienceHasResume = useSelector((state) => state.experience.has_resume);
  
  const goals = ['boostEarnings', 'similarSkills', 'manyJobs'];

  const handleGoalClick = (new_goal) => () =>  {
    trackEvent('EXPERIENCE_GOAL', new_goal);
    let value = new_goal === goal ? 'notSure' : new_goal;
    dispatch(setLoadingRecommendations(true));
    postProfile({ ...experienceForm, goal: value, skills, action: 'recommend', language: intl.locale }).then(function(response) {
      dispatch(setGoal(value));
      dispatch(setCareers(response.careers.value));
      dispatch(setJobs(response.jobs.value));
      dispatch(setTraining(response.training.value));
      dispatch(setExperienceSkills(response.skills.value));
      dispatch(setLoadingRecommendations(false));
      history.push(`${urlPrefix}/careers`);
    }).catch(function(error) {
      dispatch(setLoadingRecommendations(false));
    });
  }

  const skillsRated = skills.reduce((prev, curr) => (prev + (ratingsLookup[curr.id] !== undefined ? 1 : 0)), 0);
  console.log(skillsRated);
  const activeStep = 2 + (skillsRated >= 9 ? 1 : 0) + (experienceHasResume === true ? 1 : 0);
  const steps = ((skillsRated >= 9) && (experienceHasResume !== true)) ? (
    [
      intl.formatMessage(
        {id:'experience.steps.signedUp', description:'string'}
      ),
      intl.formatMessage(
        {id:'experience.steps.lastJob', description:'string'}
      ),
      intl.formatMessage(
        {id:'experience.steps.rated', description:'string'}
      ),
      intl.formatMessage(
        {id:'experience.steps.resume', description:'string'}
      ),
    ]
  ) : (
    [
      intl.formatMessage(
        {id:'experience.steps.signedUp', description:'string'}
      ),
      intl.formatMessage(
        {id:'experience.steps.lastJob', description:'string'}
      ),
      intl.formatMessage(
        {id:'experience.steps.resume', description:'string'}
      ),
      intl.formatMessage(
        {id:'experience.steps.rated', description:'string'}
      ),
    ]
  );

  const handleLikeClick = (item) => () => {
    // TODO: rerun recommendation
    let previousRating = ratingsLookup[item.id];
    dispatch(thumbsUpClick({ ...item, rating: previousRating }));
    rateItem(item.id, 'liked', item, previousRating === 'liked').catch(() => {
      // We revert the UI changes in case of error
      dispatch(revertThumbsClick({ ...item, rating: previousRating }));
      enqueueSnackbar(intl.formatMessage({id:'app.error'}), {variant: 'error'});
    })
    trackEvent('EXPERIENCE_SKILLS_THUMBSUP', item);
  }

  const handleDislikeClick = (item) => () => {
    // TODO: rerun recommendation
    let previousRating = ratingsLookup[item.id];
    dispatch(thumbsDownClick({ ...item, rating: previousRating }));
    rateItem(item.id, 'disliked', item, previousRating === 'disliked').catch(() => {
      // We revert the UI changes in case of error
      dispatch(revertThumbsClick({ ...item, rating: previousRating }));
      enqueueSnackbar(intl.formatMessage({id:'app.error'}), {variant: 'error'});
    })
    trackEvent('EXPERIENCE_SKILLS_THUMBSDOWN', item);
  }

  useEffect(() => {
    TimeMe.stopTimer();
    TimeMe.setCurrentPageName("SKILLS");
    TimeMe.startTimer();
    return () => {
      console.log('Leaving Skills')
    };
  }, []);


  const handleDrop = (contents, ext) => {
    trackEvent('EXPERIENCE_RESUME_UPLOAD');
    setUploading(true);
    postUpload({ contents, ext, type: 'resume' }).then(function(response) {
      let values = { ...experienceForm, ...response.experience.value, goal, skills, action: 'recommend', language: intl.locale };
      dispatch(setExperienceForm(values));
      dispatch(setExperienceHasResume(true));
      setUploading(false);
      enqueueSnackbar(intl.formatMessage({id: 'experience.uploaded'}), {variant: 'success'});
      dispatch(setLoadingRecommendations(true));
      postProfile(values).then(function(new_response) {
        dispatch(setCareers(new_response.careers.value));
        dispatch(setJobs(new_response.jobs.value));
        dispatch(setTraining(new_response.training.value));
        dispatch(setExperienceSkills(new_response.skills.value));
        dispatch(setLoadingRecommendations(false));
      }).catch(function(error) {
        enqueueSnackbar(intl.formatMessage({id: 'app.error'}), {variant: 'error'});
        dispatch(setLoadingRecommendations(false));
      });
    }).catch(function(error) {
      enqueueSnackbar(intl.formatMessage({id: 'app.error'}), {variant: 'error'});
      setUploading(false);
    });
  }

  return (
    <>
      <Modal
        className={formStyles.modal}
        open={uploading}
        onClose={() => {}}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={uploading}>
          <div className={formStyles.modalPaper}>
            <Typography variant="body2" align="center">
              {intl.formatMessage(
                {id: 'experience.uploading'}
              )}
            </Typography>
            <Box m={2} />
            <CircularProgress />
          </div>
        </Fade>
      </Modal>
      <div className={styles.content}>
        <Container maxWidth="lg">
          <div className={styles.titleFilters}>
            <Typography variant="h1" component="h2">
              {intl.formatMessage(
                {id:'experience.title', description:'string'}
              )}
            </Typography>
            <div className={styles.stepperContainer}>
            <Stepper
              classes={{
                alternativeLabel: stepperStyles.alternativeLabel
              }}
              alternativeLabel
              activeStep={activeStep}
              connector={(
                <StepConnector classes={{
                  root: stepperStyles.connectorRoot,
                  active: stepperStyles.connectorActive,
                  completed: stepperStyles.connectorCompleted,
                  line: stepperStyles.connectorLine
                }}/>
            )}>
              {steps.map((label) => (
                <Step
                  classes={{
                    root: stepperStyles.stepRoot
                  }}
                  key={label}
                >
                  <StepLabel
                    StepIconComponent={StepIcon}
                    classes={{
                      label: stepperStyles.stepLabel
                    }}
                  >{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            </div>
          </div>
          <hr className={layoutStyles.separator} />
          <Box m={4} />
          <Typography variant="h3" gutterBottom>
            {intl.formatMessage(
              {id:'experience.ratingsTitle'}
            )}
          </Typography>
          <div className={cardsStyles.container}>
            { skills && skills.map(item => (
              <SkillCard
                item={item}
                rating={ratingsLookup[item.id]}
                onLikeClick={handleLikeClick(item)}
                onDislikeClick={handleDislikeClick(item)}
              />
            ))}
            { false && (
              <div className={cardsStyles.doneCard}>
                <Typography variant="body2" gutterBottom>
                  {intl.formatMessage(
                    {id:'experience.doneForToday'}
                  )}
                </Typography>
              </div>
            )}
          </div>
          <Box m={4} />
          <hr className={layoutStyles.separator} />
          <Box m={4} />
          <Typography variant="h3" gutterBottom>
            {intl.formatMessage(
              {id:'onboarding.goals.title'}
            )}
          </Typography>
          <div className={cn(industryCardsStyles.container, cardsStyles.goalCardsContainer)}>
            {goals.map(i => (
              <GoalCard
                type={i}
                key={i}
                onClick={handleGoalClick(i)}
                active={i === goal}
              />
            ))}
          </div>
          <Box m={4} />
          <hr className={layoutStyles.separator} />
          <Box m={4} />
          <Typography variant="h3" gutterBottom>
            {intl.formatMessage(
              {id:'experience.formSectionTitle', description:'string'}
            )}
          </Typography>
          <div className={formStyles.container}>
            <div className={formStyles.dragNDropContainer}>
              <div className={formStyles.dropField}>
                <Formik
                  enableReinitialize={true}
                  initialValues={{files: []}}
                  onSubmit={() => {}}
                >
                  {({
                      errors,
                      handleBlur,
                      handleChange,
                      handleSubmit,
                      touched,
                      isSubmitting,
                      values,
                      submitForm,
                      setFieldValue
                  }) => {
                    return (
                      <form style={{width: '100%'}} onSubmit={handleSubmit}>
                        <Field
                          name="uploadresume"
                          onDrop={handleDrop}
                          component={UploadResume}
                        />
                      </form>
                    )
                  }}
                </Formik>
              </div>
            </div>
            <ExperienceForm />
          </div>
        </Container>
      </div>
    </>
  )
}

export default Experience;